import type { Card, DrawRecord, StageRecord, DrawVisual } from '../shared/types';
import { layoutFan } from './fan-layout';
import { renderCardAgeLabel } from './card-age';
import { fitCardText, fitCardTextDuringMotion } from './card-text-layout';

export type DrawPhase = 'deck' | 'opening' | 'choosing' | 'selected' | 'closingOthers' | 'moving' | 'flipping' | 'revealed' | 'settled';
export interface DrawContext {
  key: string;
  kind: 'setback' | 'questions';
  own: boolean;
  canAct: boolean;
  pending?:boolean;
  record: StageRecord;
  cards: Map<string, Card>;
  now: () => number;
  visual:DrawVisual|null;
  deckSize?:number;
}
interface InteractionState { phase: DrawPhase; questionDrawIndex: number; selectedSlot: number | null; requestId: string | null }
const escape = (value: string) => value.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]!);
const bi = (zh: string, en: string) => `${escape(zh)}<span class="en">${escape(en)}</span>`;

/** The selected button is kept, animated, and finally moved into the results area. */
export class DrawInteraction {
  readonly root = document.createElement('section');
  readonly state: InteractionState = { phase:'deck', questionDrawIndex:0, selectedSlot:null, requestId:null };
  private context: DrawContext;
  private readonly surface: HTMLElement;
  private readonly results: HTMLElement;
  private readonly caption: HTMLElement;
  private fan: HTMLButtonElement[] = [];
  private resultNodes: HTMLButtonElement[] = [];
  private generation = 0;
  private animations = new Set<Animation>();
  private resultWaiter: ((draw: { card:Card; record:DrawRecord } | null)=>void) | null = null;
  private lastConsumedRequest: string | null = null;
  private lastSelectedVisualSequence: number | null = null;
  private questionRound=0;
  private readonly redrawSelection=new Set<number>();
  private readonly reduced = matchMedia('(prefers-reduced-motion: reduce)');
  private static backReady:Promise<void>|null=null;
  private readonly backReady:Promise<void>;

  constructor(context: DrawContext, private readonly request: (kind:'setback'|'questions',index:number)=>string, private readonly onSettled:()=>void,private readonly publishVisual:(visual:DrawVisual|null)=>void=()=>{}) {
    this.context=context;
    this.questionRound=context.record.questionRedraws?.length ?? 0;
    this.root.className='draw-interaction'; this.root.dataset.drawKey=context.key;
    this.root.dataset.drawKind=context.kind;
    if(!DrawInteraction.backReady){
      const image=document.createElement('img');image.src='/card-back.svg';
      DrawInteraction.backReady=typeof image.decode==='function'?image.decode().catch(()=>{}):Promise.resolve();
    }
    this.root.classList.add('card-back-loading');
    this.backReady=DrawInteraction.backReady.then(()=>{this.root.classList.remove('card-back-loading');});
    this.root.innerHTML='<div class="draw-results"></div><div class="draw-surface"></div><div class="draw-instruction" role="status" aria-live="polite"></div>';
    this.surface=this.root.querySelector<HTMLElement>('.draw-surface')!;
    this.results=this.root.querySelector<HTMLElement>('.draw-results')!;
    this.caption=this.root.querySelector<HTMLElement>('.draw-instruction')!;
    this.root.addEventListener('click',event=>{
      const button=(event.target as HTMLElement).closest<HTMLButtonElement>('button');
      if(!button) return;
      event.stopPropagation();
      if(!this.context.canAct || button.disabled) return;
      if(button.dataset.drawOpen !== undefined) void this.open();
      else if(button.dataset.visualSlot !== undefined && this.state.phase==='choosing') void this.select(Number(button.dataset.visualSlot));
      else if(button.dataset.questionIndex!==undefined && this.canSelectQuestions()){
        const index=Number(button.dataset.questionIndex);
        if(this.redrawSelection.has(index))this.redrawSelection.delete(index);else this.redrawSelection.add(index);
        this.updateControls();this.onSettled();
      }
    });
  }

  sync(context: DrawContext): void {
    this.context=context;
    if(context.kind==='questions' && this.questionRound!==(context.record.questionRedraws?.length ?? 0)){
      this.questionRound=context.record.questionRedraws?.length ?? 0;
      this.redrawSelection.clear();
      const indices=context.record.questionRedraws?.at(-1)?.indices;
      if(indices && this.resultNodes.length===3){void this.replaceQuestions(indices);return;}
      ++this.generation;this.animations.forEach(animation=>animation.cancel());
      this.resultWaiter?.(null);this.resultWaiter=null;
      this.resultNodes=[];this.fan=[];this.results.innerHTML='';this.results.dataset.count='0';
      this.lastConsumedRequest=null;this.lastSelectedVisualSequence=null;
      this.state.questionDrawIndex=0;this.state.selectedSlot=null;this.state.requestId=null;
      this.root.classList.remove('question-collecting','question-revealing');
      this.buildDeck();this.setPhase('deck');
    }
    const ids=this.officialIds();
    const latest=context.kind==='setback' ? context.record.setbackDraw : context.record.questionDraw;
    const newest=ids[ids.length-1];
    // A disconnected player may have picked a visual slot without sending the draw.
    // Clear that temporary motion; an actual server draw is always kept.
    if(!context.own && !context.visual && this.resultWaiter && ids.length===this.resultNodes.length) {
      ++this.generation;this.animations.forEach(animation=>animation.cancel());
      this.resultWaiter(null);this.resultWaiter=null;this.state.requestId=null;
      this.buildDeck();this.setPhase('deck');
    }
    if(this.resultWaiter && latest && newest && ids.length>this.resultNodes.length && (latest.requestId===this.state.requestId || (!context.own && this.state.requestId===null))) {
      const pendingIndex=this.resultNodes.length;
      const record=context.kind==='questions' ? context.record.questionDraws?.[pendingIndex] ?? latest : latest;
      const card=context.cards.get(context.kind==='questions' ? ids[pendingIndex]! : newest);
      if(card) { this.state.requestId=record.requestId;const resolve=this.resultWaiter;this.resultWaiter=null;resolve({card,record}); }
    }
    if(context.kind==='questions'){this.syncQuestions();return;}
    if(!context.own && context.visual && context.visual.kind===context.kind && ids.length===this.resultNodes.length) {
      if(context.visual.phase==='selected' && context.visual.sequence!==this.lastSelectedVisualSequence && ['deck','choosing'].includes(this.state.phase)) {
        if(this.state.phase==='deck') {this.buildFan(context.visual.count);this.fan.forEach(b=>b.style.transform=this.fanTransform(b));this.setPhase('choosing');}
        void this.select(context.visual.slot!,undefined,true);return;
      }
      if(['opening','choosing'].includes(context.visual.phase) && this.state.phase==='deck') {void this.open(context.visual);return;}
    }
    if(!context.own && !context.visual && this.state.phase==='choosing' && ids.length===this.resultNodes.length){this.fan=[];this.buildDeck();this.setPhase('deck');}
    if(!context.own && this.state.phase==='choosing' && latest && ids.length>this.resultNodes.length) {
      void this.select(context.visual?.slot ?? Math.floor(this.fan.length/2),latest);return;
    }
    if(['selected','closingOthers','moving','flipping','revealed','opening','choosing'].includes(this.state.phase)) {
      // Every WebSocket render reattaches this same root, including the selected node.
      this.updateControls(); return;
    }
    if(ids.length>this.resultNodes.length) {
      if(latest && latest.requestId!==this.lastConsumedRequest && latest.revealAt>context.now()) {
        for(let index=this.resultNodes.length;index<ids.length-1;index++)this.restoreCard(ids[index]!,index);
        this.buildFan(context.visual?.count); this.setPhase('choosing');
        void this.select(context.visual?.slot ?? Math.floor(this.fan.length/2),latest);
        return;
      }
      for(let index=this.resultNodes.length;index<ids.length;index++)this.restoreCard(ids[index]!,index);
      this.lastConsumedRequest=latest?.requestId ?? null;
    }
    this.state.questionDrawIndex=0;
    const finished=this.resultNodes.length >= 1;
    this.setPhase(finished ? 'settled' : 'deck');
    if(!finished && !this.fan.length)this.buildDeck();
    this.updateControls();
  }

  selectedQuestionIndices():number[]{return [...this.redrawSelection].sort();}
  private canSelectQuestions():boolean {
    return this.context.kind==='questions' && this.context.canAct && !this.context.pending && this.context.own && this.state.phase==='settled' && !this.context.record.questionsCompleted && this.context.record.questionIds.length===3 && this.context.now()>=(this.latestRecord()?.revealAt ?? 0);
  }
  private async replaceQuestions(indices:number[]):Promise<void>{
    const generation=++this.generation;
    this.animations.forEach(animation=>animation.cancel());
    this.lastSelectedVisualSequence=null;
    this.root.classList.add('question-replacing');this.setPhase('flipping');this.onSettled();
    const cards=indices.map(index=>({index,button:this.resultNodes[index]!}));
    // Retain the same paper, position and text box. Only selected cards turn
    // face down; update their hidden fronts, then turn them face up together.
    await Promise.all(cards.map(({button})=>this.motion(button.querySelector<HTMLElement>('.paper-turner')!,[{transform:'rotateY(180deg)'},{transform:'rotateY(0deg)'}],200)));
    if(generation!==this.generation)return;
    cards.forEach(({index,button})=>this.writeFace(button,this.context.cards.get(this.officialIds()[index]!)!,index,false));
    await Promise.all(cards.map(({button})=>fitCardTextDuringMotion(button.querySelector<HTMLElement>('.paper-front')!,()=>generation===this.generation)));
    if(generation!==this.generation)return;
    await Promise.all(cards.map(({button})=>this.flip(button.querySelector<HTMLElement>('.paper-turner')!)));
    if(generation!==this.generation)return;
    this.setPhase('revealed');
    const remaining=(this.latestRecord()?.revealAt ?? 0)-this.context.now();
    if(remaining>0)await new Promise(done=>setTimeout(done,remaining));
    if(generation!==this.generation)return;
    this.root.classList.remove('question-replacing');this.lastConsumedRequest=this.latestRecord()?.requestId ?? null;
    this.setPhase('settled');this.onSettled();
  }
  private officialIds(): string[] { return this.context.kind==='setback' ? this.context.record.setbackId ? [this.context.record.setbackId] : [] : this.context.record.questionIds; }
  private syncQuestions():void {
    const {visual,own}=this.context,ids=this.officialIds(),latest=this.latestRecord();
    if(!own && visual?.phase==='selected' && visual.sequence!==this.lastSelectedVisualSequence && ['deck','choosing'].includes(this.state.phase)){
      const index=visual.questionIndex ?? Math.max(0,ids.length-1);
      for(let i=this.resultNodes.length;i<Math.min(index,ids.length);i++)this.restoreQuestionBack(i);
      if(index<this.resultNodes.length){this.lastSelectedVisualSequence=visual.sequence;this.syncQuestions();return;}
      if(this.state.phase==='deck' || !this.fan.length){
        this.root.classList.add('question-collecting');this.buildFan(visual.count);
        this.fan.forEach(b=>b.style.transform=this.fanTransform(b));this.setPhase('choosing');
      }
      this.lastSelectedVisualSequence=visual.sequence;
      void this.selectQuestion(visual.slot!,ids.length>index ? this.context.record.questionDraws?.[index] ?? latest ?? undefined : undefined,true);return;
    }
    if(['opening','selected','closingOthers','moving','flipping','revealed'].includes(this.state.phase)){this.updateControls();return;}
    for(let i=this.resultNodes.length;i<ids.length;i++)this.restoreQuestionBack(i);
    this.state.questionDrawIndex=this.resultNodes.length;
    if(ids.length===3){
      if(latest && latest.revealAt>this.context.now() && latest.requestId!==this.lastConsumedRequest && !this.context.record.questionRedraws?.at(-1)?.indices){void this.revealQuestions();return;}
      this.resultNodes.forEach((b,i)=>{if(b.dataset.faceId!==ids[i])this.writeFace(b,this.context.cards.get(ids[i]!)!,i);b.classList.remove('question-pending');b.querySelector<HTMLElement>('.paper-front')!.tabIndex=0;b.querySelector<HTMLElement>('.paper-turner')!.style.transform='rotateY(180deg)';});
      this.root.classList.remove('question-collecting','question-revealing');this.fan=[];
      this.setPhase('settled');return;
    }
    if(!own && !visual){
      if(this.fan.length || !this.surface.childElementCount)this.buildDeck();this.setPhase('deck');return;
    }
    if(!own && visual && ['opening','choosing'].includes(visual.phase) && this.state.phase==='deck' && !ids.length){void this.open(visual);return;}
    if(ids.length || visual?.phase==='choosing'){
      this.root.classList.add('question-collecting');
      if(!this.fan.length){this.buildFan(visual?.count ?? this.context.deckSize);this.fan.forEach(b=>b.style.transform=this.fanTransform(b));}
      this.setPhase('choosing');return;
    }
    if(!this.surface.childElementCount)this.buildDeck();this.setPhase('deck');
  }
  private restoreQuestionBack(index:number):void {
    this.root.classList.add('question-collecting');
    const button=this.makeCard(index);button.classList.add('question-pending');
    this.finishCard(button,36);this.resultNodes.push(button);
  }
  private retainQuestionFan():void {
    this.fan.forEach((b,slot)=>{
      // Compact protocol indices only. Preserve each physical node's original
      // transform and layer, leaving a hole where the selected card used to be.
      b.dataset.visualSlot=String(slot);
      b.setAttribute('aria-label',`选择第 ${slot+1} 张背面牌 / Choose face-down card ${slot+1}`);
    });
  }
  private async selectQuestion(slot:number,broadcast?:DrawRecord,watch=false):Promise<void> {
    if(this.state.phase!=='choosing' || (!broadcast && !watch && !this.context.canAct))return;
    const picked=this.fan[slot];if(!picked)return;
    const generation=++this.generation;this.state.selectedSlot=slot;this.setPhase('selected');
    picked.style.zIndex='30';picked.setAttribute('aria-pressed','true');picked.dataset.picked='true';
    let resolve!:(value:{card:Card;record:DrawRecord}|null)=>void;
    const result=new Promise<{card:Card;record:DrawRecord}|null>(done=>{resolve=done;});this.resultWaiter=resolve;
    if(broadcast){this.state.requestId=broadcast.requestId;this.resultWaiter=null;resolve({card:this.context.cards.get(this.officialIds().at(-1)!)!,record:broadcast});}
    else if(!watch){
      this.publishVisual({kind:'questions',phase:'selected',count:this.context.deckSize ?? this.fan.length,slot,sequence:Date.now(),questionIndex:this.resultNodes.length});
      this.state.requestId=this.request('questions',this.resultNodes.length);
    }
    await this.motion(picked,[{transform:getComputedStyle(picked).transform},{transform:`translate(${picked.style.getPropertyValue('--fan-x')},${parseFloat(picked.style.getPropertyValue('--fan-y'))-22}px) rotate(0deg) scale(1.06)`}],140);
    const actual=await result;if(generation!==this.generation || !actual)return;
    this.setPhase('moving');const old=picked.getBoundingClientRect();
    picked.classList.add('question-pending');this.finishCard(picked,36);this.resultNodes.push(picked);
    this.fan=this.fan.filter(b=>b!==picked);this.state.questionDrawIndex=this.resultNodes.length;
    const target=picked.getBoundingClientRect();
    await this.motion(picked,[{transform:`translate(${old.left-target.left}px,${old.top-target.top}px) scale(${old.width/target.width})`},{transform:'translate(0,0) scale(1)'}],280);
    if(generation!==this.generation)return;
    if(this.resultNodes.length===3){await this.revealQuestions(generation);return;}
    this.retainQuestionFan();if(generation!==this.generation)return;
    this.state.requestId=null;this.state.selectedSlot=null;this.setPhase('choosing');
    if(!watch)this.publishVisual({kind:'questions',phase:'choosing',count:this.context.deckSize ?? this.fan.length,slot:null,sequence:Date.now(),questionIndex:this.resultNodes.length});
    this.onSettled();
  }
  private async revealQuestions(generation=++this.generation):Promise<void> {
    this.setPhase('closingOthers');
    await Promise.all(this.fan.map(b=>this.motion(b,[{transform:this.fanTransform(b),opacity:1},{transform:'translate(0,0) scale(.9)',opacity:0}],260)));
    if(generation!==this.generation)return;
    const positions=this.resultNodes.map(b=>b.getBoundingClientRect());this.fan=[];this.surface.innerHTML='';
    this.root.classList.remove('question-collecting');this.root.classList.add('question-revealing');this.setPhase('moving');
    // Prepare the hidden front before starting one continuous turn. Pausing
    // edge-on for DOM/font layout made the old two-half flip feel stuck.
    this.resultNodes.forEach((b,i)=>this.writeFace(b,this.context.cards.get(this.officialIds()[i]!)!,i,false));
    await Promise.all([
      ...this.resultNodes.map((b,i)=>{const from=positions[i]!,to=b.getBoundingClientRect();return this.motion(b,[{transform:`translate(${from.left-to.left}px,${from.top-to.top}px) scale(${from.width/to.width})`},{transform:'translate(0,0) scale(1)'}],320);}),
      ...this.resultNodes.map(b=>fitCardTextDuringMotion(b.querySelector<HTMLElement>('.paper-front')!,()=>generation===this.generation)),
    ]);
    if(generation!==this.generation)return;
    this.setPhase('flipping');
    await Promise.all(this.resultNodes.map(b=>this.flip(b.querySelector<HTMLElement>('.paper-turner')!)));
    if(generation!==this.generation)return;
    this.setPhase('revealed');const remaining=(this.latestRecord()?.revealAt ?? 0)-this.context.now();
    if(remaining>0)await new Promise(done=>setTimeout(done,remaining));if(generation!==this.generation)return;
    this.resultNodes.forEach(b=>{b.classList.remove('question-pending');b.querySelector<HTMLElement>('.paper-front')!.tabIndex=0;});
    this.root.classList.remove('question-revealing');this.state.requestId=null;this.state.selectedSlot=null;
    this.lastConsumedRequest=this.latestRecord()?.requestId ?? null;this.setPhase('settled');
    if(this.context.own && this.context.canAct)this.publishVisual(null);this.onSettled();
  }
  private setPhase(phase: DrawPhase): void {
    this.state.phase=phase;this.root.dataset.drawPhase=phase;
    // A setback adopts its final paper layout while it is still face down.
    // The action remains busy until the flip finishes; layout is independent.
    this.root.dataset.drawLayout=phase==='settled' || this.root.classList.contains('question-replacing') || (this.context.kind==='setback' && this.resultNodes.length>0) ? 'settled' : 'drawing';
    this.root.dataset.questionDrawIndex=String(this.state.questionDrawIndex);
    this.updateControls();
  }
  private updateControls(): void {
    const phase=this.state.phase;
    for(const button of this.fan)button.disabled=phase!=='choosing' || !this.context.canAct;
    if(this.context.kind==='questions'){
      const selectable=this.canSelectQuestions();
      if(!this.context.canAct || this.context.record.questionsCompleted)this.redrawSelection.clear();
      this.resultNodes.forEach((button,index)=>{
        button.dataset.questionIndex=String(index);button.tabIndex=selectable?0:-1;
        button.classList.toggle('question-redraw-selectable',selectable);
        button.classList.toggle('question-redraw-selected',selectable && this.redrawSelection.has(index));
        if(phase==='settled')button.querySelector<HTMLElement>('.paper-front')!.tabIndex=selectable?-1:0;
        if(selectable){button.removeAttribute('aria-disabled');button.setAttribute('aria-pressed',String(this.redrawSelection.has(index)));}
        else {button.setAttribute('aria-disabled','true');button.removeAttribute('aria-pressed');}
      });
    }
    if(phase==='settled') { this.surface.hidden=true;this.caption.innerHTML=this.canSelectQuestions()?bi(this.redrawSelection.size?`已选 ${this.redrawSelection.size} 张，点击“重新抽取”替换。`:'可点击不合适的问题牌，选择重抽。',this.redrawSelection.size?`${this.redrawSelection.size} selected. Choose Redraw to replace them.`:'Tap any questions you would like to replace.') : bi(this.context.kind==='questions'?'三张问题已抽齐，请慢慢阅读。':'牌已翻开，请继续你的选择。',this.context.kind==='questions'?'All three questions are ready to read.':'The card is revealed. Continue with your choice.');return; }
    this.surface.hidden=(this.context.kind==='setback' && this.resultNodes.length>0) || (this.context.kind==='questions' && this.resultNodes.length===3 && ['moving','flipping','revealed'].includes(phase));
    if(phase==='deck')this.caption.innerHTML=this.context.deckSize===0 ? bi('本阶段没有可抽的牌。','No cards remain in this stage.') : this.context.canAct ? `<button data-draw-open>${bi(this.context.kind==='questions'?`抽取问题 ${this.resultNodes.length+1} / 3`:'抽牌','Draw')}</button>` : bi('等待当前玩家亲自抽牌。','Waiting for the current player to draw.');
    else if(phase==='opening' || phase==='choosing')this.caption.innerHTML=this.context.kind==='questions'?bi('从上面的问答牌中抽取三张。','Draw three cards from the question cards above.'):bi('请抽取一张挫折牌','Please draw one setback card.');
    else if(phase==='selected' || phase==='closingOthers' || phase==='moving')this.caption.innerHTML=bi('这一张，交到你手中。','Your chosen card comes to the center.');
    else if(phase==='flipping')this.caption.innerHTML=bi('轻轻翻开。','Turn it over.');
    else if(phase==='revealed')this.caption.innerHTML=bi('牌已翻开。','Card revealed.');
  }
  private backMarkup(): string { return `<span class="paper-back"><b>◇</b><small>${this.context.kind==='setback'?'挫折牌 · SETBACK':'问答牌 · QUESTION'}</small></span><span class="paper-front"></span>`; }
  private makeCard(slot: number): HTMLButtonElement {
    const button=document.createElement('button');button.type='button';button.className='physical-card';
    button.dataset.visualSlot=String(slot);button.dataset.physicalId=crypto.randomUUID();
    button.setAttribute('aria-label',`选择第 ${slot+1} 张背面牌 / Choose face-down card ${slot+1}`);
    button.innerHTML=`<span class="paper-turner">${this.backMarkup()}</span>`;
    return button;
  }
  private buildDeck(): void {
    this.surface.innerHTML='<button class="physical-deck" data-draw-open aria-label="展开牌堆 / Open the deck"><i></i><i></i><span>◇<small>点开牌堆 / Open deck</small></span></button>';
    this.surface.style.height='220px';this.fan=[];delete this.root.dataset.fanOverflow;
    const deck=this.surface.querySelector<HTMLButtonElement>('button')!;deck.disabled=!this.context.canAct || this.context.deckSize===0;
  }
  private buildFan(visualCount?:number): void {
    this.surface.innerHTML='';
    const count=visualCount ?? this.context.deckSize ?? 7;
    const layout=layoutFan(count,this.root.clientWidth);
    this.root.style.setProperty('--pick-width',`${layout.cardWidth}px`);
    this.surface.style.height=`${layout.height}px`;
    this.root.dataset.fanCount=String(count);this.root.dataset.fanRows=String(layout.rows);
    this.root.dataset.fanOverflow=String(layout.height+48>(this.root.closest('.table-stage')?.clientHeight ?? Infinity));
    this.fan=layout.cards.map(({slot,row,x,y,angle})=>{
      const button=this.makeCard(slot);button.dataset.fanRow=String(row);
      button.style.zIndex=String(slot+1);button.style.setProperty('--fan-layer',String(slot+1));
      button.style.setProperty('--fan-x',`${x}px`);button.style.setProperty('--fan-y',`${y}px`);button.style.setProperty('--fan-angle',`${angle}deg`);
      this.surface.appendChild(button);return button;
    });
  }
  private async motion(element: HTMLElement, frames: Keyframe[], duration: number): Promise<void> {
    const animation=element.animate(frames,{duration:this.reduced.matches?60:duration,easing:element.classList.contains('paper-turner')?'cubic-bezier(.35,0,.65,1)':'cubic-bezier(.25,.65,.3,1)',fill:'forwards'});
    this.animations.add(animation);
    try { await animation.finished; const last=frames[frames.length-1]!;if(last.transform)element.style.transform=String(last.transform);if(last.opacity!==undefined)element.style.opacity=String(last.opacity); }
    catch { /* A player switched views or reconnected. Official cards are restored from the snapshot. */ }
    finally { animation.cancel();this.animations.delete(animation); }
  }
  private async flip(turner:HTMLElement):Promise<void> {
    turner.style.willChange=this.reduced.matches?'opacity':'transform';
    if(this.reduced.matches){turner.style.transform='rotateY(180deg)';await this.motion(turner,[{opacity:0},{opacity:1}],80);}
    else await this.motion(turner,[{transform:'rotateY(0deg)'},{transform:'rotateY(180deg)'}],360);
    turner.style.removeProperty('will-change');
  }
  private fanTransform(button: HTMLButtonElement): string { return `translate(${button.style.getPropertyValue('--fan-x')},${button.style.getPropertyValue('--fan-y')}) rotate(${button.style.getPropertyValue('--fan-angle')})`; }
  private async open(watch?:DrawVisual): Promise<void> {
    if(this.state.phase!=='deck' || (!watch && (!this.context.canAct || this.context.deckSize===0)))return;
    const generation=++this.generation;
    this.setPhase('opening');
    await this.backReady;if(generation!==this.generation)return;
    if(this.context.kind==='questions')this.root.classList.add('question-collecting');
    this.buildFan(watch?.count);this.setPhase('opening');
    if(!watch)this.publishVisual({kind:this.context.kind,phase:'opening',count:this.fan.length,slot:null,sequence:Date.now(),...(this.context.kind==='questions'?{questionIndex:this.resultNodes.length}:{})});
    await Promise.all(this.fan.map(button=>this.motion(button,[{transform:'translate(0,0) rotate(-3deg)'},{transform:this.fanTransform(button)}],350)));
    if(generation===this.generation){this.setPhase('choosing');if(watch)this.sync(this.context);else this.publishVisual({kind:this.context.kind,phase:'choosing',count:this.fan.length,slot:null,sequence:Date.now(),...(this.context.kind==='questions'?{questionIndex:this.resultNodes.length}:{})});}
  }
  private async select(slot: number, broadcast?: DrawRecord,watch=false): Promise<void> {
    if(this.context.kind==='questions'){await this.selectQuestion(slot,broadcast,watch);return;}
    if(this.state.phase!=='choosing' || (!broadcast && !watch && !this.context.canAct))return;
    const picked=this.fan[slot];if(!picked)return;
    const selectedFrom=getComputedStyle(picked).transform;
    if(this.context.visual?.phase==='selected')this.lastSelectedVisualSequence=this.context.visual.sequence;
    const generation=++this.generation;
    this.state.selectedSlot=slot;this.setPhase('selected');
    picked.dataset.picked='true';picked.style.zIndex='20';picked.setAttribute('aria-pressed','true');
    let resolve!: (value:{card:Card;record:DrawRecord}|null)=>void;
    const result=new Promise<{card:Card;record:DrawRecord}|null>(done=>{resolve=done;});this.resultWaiter=resolve;
    if(broadcast) {
      this.state.requestId=broadcast.requestId;
      const id=this.officialIds().at(-1)!;const card=this.context.cards.get(id)!;
      this.resultWaiter=null;resolve({card,record:broadcast});
    } else if(!watch) {
      // Slot is a visual choice only: no slot/card mapping is sent to the server.
      this.publishVisual({kind:this.context.kind,phase:'selected',count:this.fan.length,slot,sequence:Date.now()});
      this.state.requestId=this.request(this.context.kind,this.resultNodes.length);
    }
    const x=picked.style.getPropertyValue('--fan-x');const y=parseFloat(picked.style.getPropertyValue('--fan-y'))-18;
    const lifted=`translate(${x},${y}px) rotate(0deg) scale(1.08)`;
    await this.motion(picked,[{transform:selectedFrom},{transform:lifted}],150);
    if(generation!==this.generation)return;
    this.setPhase('closingOthers');
    await Promise.all(this.fan.filter(button=>button!==picked).map(button=>this.motion(button,[{transform:this.fanTransform(button),opacity:1},{transform:'translate(0,0) rotate(-3deg) scale(.94)',opacity:.28}],300)));
    if(generation!==this.generation)return;
    for(const button of this.fan)if(button!==picked)button.hidden=true;
    this.setPhase('moving');
    if(!this.officialIds().length || this.state.requestId!==this.latestRecord()?.requestId)this.caption.innerHTML=bi('等待服务器抽中的牌…','Waiting for the server-drawn card…');
    const actual=await result;
    if(generation!==this.generation || !actual)return;
    // Reserve the disabled decision dock before measuring the final card.
    // Travel and flip overlap the server's guard time; confirmation still
    // waits for revealAt below and remains validated on the server.
    const from=picked.getBoundingClientRect();
    picked.classList.add('setback-revealing');
    this.finishCard(picked,Math.min(270,this.root.clientWidth-24));
    this.resultNodes.push(picked);
    this.setPhase('moving');
    this.onSettled();
    const to=picked.getBoundingClientRect();
    this.writeFace(picked,actual.card,this.resultNodes.length,false);
    // Keep the same physical node and final face box. Only its outer transform
    // changes: no inverse font scaling or second paper layout after the flip.
    await Promise.all([
      this.motion(picked,[{transform:`translate(${from.left-to.left}px,${from.top-to.top}px) scale(${from.width/to.width})`},{transform:'translate(0,0) scale(1)'}],400),
      fitCardTextDuringMotion(picked.querySelector<HTMLElement>('.paper-front')!,()=>generation===this.generation),
    ]);
    if(generation!==this.generation)return;
    const turner=picked.querySelector<HTMLElement>('.paper-turner')!;
    this.setPhase('flipping');
    await this.flip(turner);
    if(generation!==this.generation)return;
    this.setPhase('revealed');
    const remaining=actual.record.revealAt-this.context.now();
    if(remaining>0)await new Promise(done=>setTimeout(done,remaining));
    if(generation!==this.generation)return;
    picked.classList.remove('setback-revealing');
    this.lastConsumedRequest=actual.record.requestId;
    this.state.questionDrawIndex=0;
    // Keep the newly drawn question in view within the horizontal hand.
    if(this.results.scrollWidth>this.results.clientWidth) {
      const hand=this.results.getBoundingClientRect();const card=picked.getBoundingClientRect();
      this.results.scrollTo({left:this.results.scrollLeft+card.left-hand.left-4,behavior:'instant'});
    }
    this.fan=[];this.state.requestId=null;this.state.selectedSlot=null;
    const finished=this.resultNodes.length>=1;
    if(!finished)this.buildDeck();
    this.setPhase(finished?'settled':'deck');if(this.context.own && this.context.canAct)this.publishVisual(null);this.onSettled();
  }
  private latestRecord(): DrawRecord | null { return this.context.kind==='setback'?this.context.record.setbackDraw:this.context.record.questionDraw; }
  private writeFace(button: HTMLButtonElement,card:Card,index:number,fit=true): void {
    button.dataset.faceId=card.id;
    const body=card.descriptionZh && card.descriptionZh!==card.titleZh ? `<p>${bi(card.descriptionZh,card.descriptionEn)}</p>` : '';
    const face=button.querySelector<HTMLElement>('.paper-front')!;
    face.innerHTML=`${renderCardAgeLabel(this.context.record.stage)}<strong>${bi(card.titleZh,card.titleEn)}</strong>${body}`;
    // Fit the final face while edge-on, before the browser paints its front.
    if(fit && this.context.kind==='setback' && getComputedStyle(face).display==='flex')fitCardText(face);
    face.dataset.scrollKey=`draw-face-${card.id}-${index}`;
    button.setAttribute('aria-label',`${card.titleZh} / ${card.titleEn}`);
  }
  private finishCard(button: HTMLButtonElement,width:number): void {
    button.classList.add('draw-result');button.disabled=false;button.removeAttribute('aria-pressed');button.setAttribute('aria-disabled','true');button.tabIndex=-1;
    button.removeAttribute('data-visual-slot');button.style.cssText=`--result-width:${width}px;--face-scale:1;transform:none;`;
    button.querySelector<HTMLElement>('.paper-front')!.tabIndex=button.classList.contains('question-pending')?-1:0;
    this.results.appendChild(button);this.results.dataset.count=String(this.resultNodes.length+1);
  }
  private restoreCard(id:string,index:number): void {
    const card=this.context.cards.get(id);if(!card)return;
    const button=this.makeCard(index);this.writeFace(button,card,index);
    button.querySelector<HTMLElement>('.paper-turner')!.style.transform='rotateY(180deg)';
    this.finishCard(button,Math.min(270,this.root.clientWidth-24));this.resultNodes.push(button);
  }
  failPending(): void {
    if(!this.state.requestId)return;
    ++this.generation;this.animations.forEach(animation=>animation.cancel());this.resultWaiter?.(null);this.resultWaiter=null;
    this.state.requestId=null;this.state.selectedSlot=null;
    this.buildFan();this.fan.forEach(button=>button.style.transform=this.fanTransform(button));this.setPhase('choosing');
  }
  dispose(): void { ++this.generation;this.animations.forEach(animation=>animation.cancel());this.resultWaiter?.(null);this.resultWaiter=null; }
}
